import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { authenticatedFetch } from "../../../network/api";

import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import ViewIcon from "./assets/viewIcon.png";
import "./settingsIntake.css";
import AiFormGenerator from "./aiFormGenerator";
import AIForm from "./assets/aiForm.png";
import BuildForm from "./assets/buildForm.png";
import UploadFormIcon from "./assets/uploadForm.png";
import UploadForm from "./uploadForm";

export default function SettingsIntake() {
  const lawyerId = useSelector((s) => s.attorneyData.attorneyData?.user_id);
  const lawyerFirstName = useSelector(
    (s) => s.attorneyData.attorneyData?.first_name
  );
  const lawyerLastName = useSelector(
    (s) => s.attorneyData.attorneyData?.last_name
  );

  const navigate = useNavigate();

  // STATE
  const [forms, setForms] = useState([]);
  const [apptTypes, setApptTypes] = useState([]);
  const [loading, setLoading] = useState(true);

  // "Create" menu
  const [createAnchor, setCreateAnchor] = useState(null);

  // Level 1 (Main Menu) State
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuFormId, setMenuFormId] = useState(null);

  // Level 2 (Submenu) State
  const [lvl2Anchor, setLvl2Anchor] = useState(null);
  const [attachMode, setAttachMode] = useState(false);

  // Level 3 (Sub-submenu) State
  const [lvl3Anchor, setLvl3Anchor] = useState(null);
  const [hoverType, setHoverType] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    typeId: null,
    typeName: "",
  });

  // For "attach" dialog
  const [checkedTypes, setCheckedTypes] = useState([]);

  // AI Form Generator popup state
  const [aiPopupOpen, setAiPopupOpen] = useState(false);
  // Upload Form popup state
  const [uploadPopupOpen, setUploadPopupOpen] = useState(false);

  // FETCH forms + appointment types on-mount
  useEffect(() => {
    if (!lawyerId) return;
    setLoading(true);

    Promise.all([
      // 1️⃣ Fetch forms (with attachedTypes baked in)
      authenticatedFetch(`/api/attorney/webapp/intake/forms/list?lawyerId=${lawyerId}`)
        .then((r) => r.json())
        .then((d) => (Array.isArray(d.forms) ? d.forms : [])),

      // 2️⃣ Fetch appointment types
      authenticatedFetch(`/api/attorney/webapp/fetchAppointmentTypes?user_id=${lawyerId}`)
        .then((r) => r.json())
        .then((d) => {
          // normalise what the API actually sends
          const raw = Array.isArray(d.appointmentTypes)
            ? d.appointmentTypes
            : [];
          return raw.map((t) => ({
            id: t.id,
            name: t.appointment_name,
          }));
        }),
    ])
      .then(([forms, types]) => {
        // Map the appointment type names to the forms
        const formsWithTypes = forms.map((form) => ({
          ...form,
          attachedTypes: form.attachedTypes.map((attachedType) => {
            const matchingType = types.find((t) => t.id === attachedType.id);
            return {
              ...attachedType,
              name: matchingType ? matchingType.name : "Unknown Type",
            };
          }),
        }));
        setForms(formsWithTypes);
        setApptTypes(types);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error loading intake settings:", err);
        setLoading(false);
      });
  }, [lawyerId]);

  // Fetch appointment types for linking
  useEffect(() => {
    if (!lawyerId) return;
    authenticatedFetch(`/api/attorney/webapp/fetchAppointmentTypes?user_id=${lawyerId}`)
      .then((res) => res.json())
      .then((json) => setApptTypes(json.appointmentTypes || []))
      .catch(console.error);
  }, [lawyerId]);

  // === Create menu handlers ===
  const openCreateMenu = (e) => setCreateAnchor(e.currentTarget);
  const closeCreateMenu = () => setCreateAnchor(null);
  const handleCreate = (opt) => {
    closeCreateMenu();
    if (opt === "Upload Intake") setUploadPopupOpen(true);
    if (opt === "Use Form Builder") navigate("/attorney/intake/form-builder");
    if (opt === "Generate with AI") setAiPopupOpen(true);
  };

  // Simplified menu handlers - click-based instead of hover
  const handleMenuClick = (e, formId) => {
    setMenuAnchor(e.currentTarget);
    setMenuFormId(formId);
  };

  const closeAllMenus = () => {
    setMenuAnchor(null);
    setMenuFormId(null);
    setLvl2Anchor(null);
    setLvl3Anchor(null);
    setAttachMode(false);
    setHoverType(null);
  };

  const handleL2MenuClick = (e, isAttach) => {
    e.stopPropagation();
    setAttachMode(isAttach);
    setLvl2Anchor(e.currentTarget);
    setLvl3Anchor(null); // Close L3 when opening L2
  };

  const handleL3MenuClick = (e, type) => {
    e.stopPropagation();
    setHoverType(type);
    setLvl3Anchor(e.currentTarget);
  };

  const handleEdit = (formId) => {
    navigate("/attorney/intake/form-builder", {
      state: { templateId: formId },
    });
    closeAllMenus();
  };

  const handleDuplicate = async (formId) => {
    await authenticatedFetch("/api/attorney/webapp/intake/forms/duplicate", {
      method: "POST",
      body: JSON.stringify({ id: formId }),
    });
    closeAllMenus();
    // simplistic reload for latest data
    window.location.reload();
  };

  const handleDelete = async (formId) => {
    try {
      const res = await authenticatedFetch(
        `/api/attorney/webapp/intake/forms/delete?id=${encodeURIComponent(formId)}`,
        { method: "DELETE" }
      );

      if (!res.ok) {
        const { error } = await res.json();
        alert("Delete failed: " + (error || res.statusText));
        return;
      }

      closeAllMenus();
      // success
      window.location.reload();
    } catch (err) {
      console.error("Delete error:", err);
      alert("Something went wrong.");
    }
  };

  const handleUnlinkAll = async (formId) => {
    await authenticatedFetch("/api/attorney/webapp/intake/forms/unlinkAll", {
      method: "POST",
      body: JSON.stringify({ templateId: formId }),
    });
    setForms((fs) =>
      fs.map((f) => (f.id === formId ? { ...f, attachedTypes: [] } : f))
    );
    closeAllMenus();
  };

  const toggleType = (typeId) => {
    const type = apptTypes.find((t) => t.id === typeId);
    const isCurrentlyChecked = checkedTypes.includes(typeId);

    if (!isCurrentlyChecked) {
      // Check if this appointment type already has a form attached to ANY other form
      const existingForm = forms.find(
        (f) =>
          f.id !== menuFormId && f.attachedTypes.some((t) => t.id === typeId)
      );

      if (existingForm) {
        setConfirmDialog({
          open: true,
          typeId,
          typeName: type.name,
          existingFormName: existingForm.name,
        });
        return;
      }
    }

    // Normal toggle
    setCheckedTypes((ct) =>
      ct.includes(typeId) ? ct.filter((x) => x !== typeId) : [...ct, typeId]
    );
  };

  const confirmAttach = async () => {
    const formId = menuFormId;
    const existing =
      forms.find((f) => f.id === formId)?.attachedTypes.map((t) => t.id) || [];
    const toLink = checkedTypes.filter((id) => !existing.includes(id));
    const toUnlink = existing.filter((id) => !checkedTypes.includes(id));

    try {
      // Show loading state
      setLoading(true);

      // Perform all link/unlink operations
      await Promise.all([
        ...toLink.map((typeId) =>
          authenticatedFetch("/api/attorney/webapp/intake/forms/link", {
            method: "POST",
            body: JSON.stringify({
              appointmentTypeId: typeId,
              templateId: formId,
            }),
          })
        ),
        ...toUnlink.map((typeId) =>
          authenticatedFetch("/api/attorney/webapp/intake/forms/unlink", {
            method: "POST",
            body: JSON.stringify({
              appointmentTypeId: typeId,
              templateId: formId,
            }),
          })
        ),
      ]);

      // Refresh the forms data from server
      const response = await authenticatedFetch(
        `/api/attorney/webapp/intake/forms/list?lawyerId=${lawyerId}`
      );
      const data = await response.json();
      const freshForms = Array.isArray(data.forms) ? data.forms : [];

      // Map the appointment type names to the fresh forms data
      const formsWithTypes = freshForms.map((form) => ({
        ...form,
        attachedTypes: form.attachedTypes.map((attachedType) => {
          const matchingType = apptTypes.find((t) => t.id === attachedType.id);
          return {
            ...attachedType,
            name: matchingType ? matchingType.name : "Unknown Type",
          };
        }),
      }));

      setForms(formsWithTypes);
      closeAllMenus();
    } catch (error) {
      console.error("Error updating form attachments:", error);
      alert("Something went wrong while updating the form attachments.");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmReplace = () => {
    const { typeId } = confirmDialog;
    setCheckedTypes((ct) => [...ct, typeId]);
    setConfirmDialog({ open: false, typeId: null, typeName: "" });
  };

  const handleUnlinkType = async (formId, typeId) => {
    await authenticatedFetch("/api/attorney/webapp/intake/forms/unlink", {
      method: "POST",
      body: JSON.stringify({ appointmentTypeId: typeId, templateId: formId }),
    });
    setForms((fs) =>
      fs.map((f) =>
        f.id === formId
          ? {
              ...f,
              attachedTypes: f.attachedTypes.filter((t) => t.id !== typeId),
            }
          : f
      )
    );
    closeAllMenus();
  };

  // Handle clicks outside menus to close them
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside all menu elements
      if (
        !event.target.closest('[role="menu"]') &&
        !event.target.closest("button")
      ) {
        closeAllMenus();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (loading) {
    return (
      <div className="intake-container">
        <div className="spinner-row">
          <CircularProgress />
        </div>
      </div>
    );
  }

  return (
    <div className="intake-container">
      {/* Header */}
      <div className="intake-header-row">
        <div className="intake-header-title">Intake Form Templates</div>
        <button
          onClick={openCreateMenu}
          style={{
            width: "234px",
            height: "48px",
            borderRadius: "4px",
            border: "1px solid #D5D4D2",
            paddingTop: "12px",
            paddingRight: "24px",
            paddingBottom: "12px",
            paddingLeft: "24px",
            background: "#144A8C",
            color: "#fff",
            fontWeight: 500,
            fontFamily: "Poppins",
            fontSize: "15px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "10px",
            opacity: 1,
          }}
        >
          Create Intake Form
          <KeyboardArrowDownIcon style={{ fontSize: "20px" }} />
        </button>
        <Menu
          anchorEl={createAnchor}
          open={Boolean(createAnchor)}
          onClose={closeCreateMenu}
          PaperProps={{
            style: {
              borderRadius: 16,
              minWidth: 270,
              boxShadow: "0 4px 16px rgba(20,74,140,0.10)",
              marginTop: 8,
              padding: 0,
              background: "#fff",
              border: "1.5px solid #e3eaf3",
            },
          }}
          MenuListProps={{
            style: {
              padding: 0,
            },
          }}
        >
          <MenuItem
            onClick={() => handleCreate("Use Form Builder")}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              fontSize: "1.08rem",
              color: "#144a8c",
              fontWeight: 500,
              fontFamily: "Poppins",
              padding: "18px 24px 14px 24px",
              borderBottom: "1px solid #f0f4f8",
              background: "#fff",
              transition: "background 0.15s",
              "&:hover": {
                background: "#f5faff",
              },
            }}
          >
            {/* Replace with icon later */}
            <img
              src={BuildForm}
              alt="Use Form Builder"
              style={{ width: 24, height: 24, marginRight: 16 }}
            />
            Use form builder
          </MenuItem>
          <MenuItem
            onClick={() => handleCreate("Generate with AI")}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              fontSize: "1.08rem",
              color: "#144a8c",
              fontWeight: 500,
              fontFamily: "Poppins",
              padding: "18px 24px 14px 24px",
              borderBottom: "1px solid #f0f4f8",
              background: "#fff",
              transition: "background 0.15s",
              "&:hover": {
                background: "#f5faff",
              },
            }}
          >
            {/* Replace with icon later */}
            <img
              src={AIForm}
              alt="Generate with AI"
              style={{ width: 24, height: 24, marginRight: 16 }}
            />
            Generate with AI
          </MenuItem>
          <MenuItem
            onClick={() => handleCreate("Upload Intake")}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              fontSize: "1.08rem",
              color: "#144a8c",
              fontWeight: 500,
              fontFamily: "Poppins",
              padding: "18px 24px 14px 24px",
              background: "#fff",
              transition: "background 0.15s",
              "&:hover": {
                background: "#f5faff",
              },
            }}
          >
            {/* Replace with icon later */}
            <img
              src={UploadFormIcon}
              alt="Upload Intake"
              style={{ width: 24, height: 24, marginRight: 16 }}
            />
            Upload
          </MenuItem>
        </Menu>
      </div>
      <div className="intake-header-desc">
        Manage all your intake form templates in one place. Attach them to
        appointment types or customize them before sending to clients.
      </div>

      {/* Search & Filters */}
      <div className="intake-search-row">
        <div className="intake-search-box">
          <span className="intake-search-icon">🔍</span>
          <input
            className="intake-search-input"
            placeholder="Search by intake form name or linked appointment type..."
          />
        </div>
        <button className="intake-filter-btn">Form Type</button>
        <button className="intake-filter-btn">Usage</button>
      </div>

      {/* Cards */}
      <div className="intake-cards-row">
        {forms.map((form) => (
          <div className="intake-card" key={form.id}>
            {/* Title & Card Menu */}
            <div className="intake-card-title-row">
              <div className="intake-card-title">{form.name}</div>

              {/* Level 1 Menu */}
              <Menu
                anchorEl={menuAnchor}
                open={menuFormId === form.id}
                onClose={closeAllMenus}
                PaperProps={{
                  style: {
                    borderRadius: 8,
                    minWidth: 220,
                    boxShadow:
                      "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                    marginTop: 8,
                  },
                }}
              >
                <MenuItem onClick={() => handleEdit(form.id)}>Edit</MenuItem>

                {form.attachedTypes.length ? (
                  <MenuItem onClick={(e) => handleL2MenuClick(e, false)}>
                    View Linked Types ▸
                  </MenuItem>
                ) : (
                  <MenuItem onClick={(e) => handleL2MenuClick(e, true)}>
                    Attach to Appointment Type ▸
                  </MenuItem>
                )}

                {form.attachedTypes.length > 0 && (
                  <MenuItem onClick={() => handleUnlinkAll(form.id)}>
                    Unlink All
                  </MenuItem>
                )}

                <MenuItem onClick={() => handleDelete(form.id)}>
                  Delete
                </MenuItem>

                <MenuItem onClick={() => handleDuplicate(form.id)}>
                  Duplicate
                </MenuItem>
              </Menu>

              {/* Level 2 Menu */}
              <Menu
                anchorEl={lvl2Anchor}
                open={Boolean(lvl2Anchor)}
                onClose={closeAllMenus}
                anchorOrigin={{ horizontal: "right", vertical: "top" }}
                transformOrigin={{ horizontal: "left", vertical: "top" }}
                PaperProps={{
                  style: {
                    borderRadius: 8,
                    minWidth: 220,
                    boxShadow:
                      "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                    marginLeft: 8,
                  },
                }}
              >
                {attachMode && (
                  <>
                    <MenuItem
                      disabled
                      sx={{
                        fontSize: "0.8rem",
                        color: "text.secondary",
                        whiteSpace: "normal",
                        padding: "8px 16px",
                      }}
                    >
                      ℹ️ Each appointment type can only have one active intake
                      form. Selecting a type that already has a form will
                      replace it.
                    </MenuItem>
                    <MenuItem disabled>────────────</MenuItem>
                  </>
                )}

                {(attachMode
                  ? apptTypes
                  : (forms.find((f) => f.id === menuFormId)?.attachedTypes ??
                    [])
                ).map((t) => (
                  <MenuItem
                    key={t.id}
                    onClick={
                      attachMode
                        ? () => toggleType(t.id)
                        : (e) => handleL3MenuClick(e, t)
                    }
                  >
                    {attachMode && (
                      <Checkbox
                        checked={checkedTypes.includes(t.id)}
                        size="small"
                        sx={{ mr: 1 }}
                      />
                    )}
                    {t.name} {!attachMode && "▸"}
                  </MenuItem>
                ))}

                {attachMode && <MenuItem disabled>────────────</MenuItem>}
                {attachMode && (
                  <MenuItem
                    disabled={!checkedTypes.length}
                    onClick={confirmAttach}
                  >
                    Confirm Selection
                  </MenuItem>
                )}
              </Menu>

              {/* Level 3 Menu */}
              <Menu
                anchorEl={lvl3Anchor}
                open={!attachMode && Boolean(lvl3Anchor)}
                onClose={closeAllMenus}
                anchorOrigin={{ horizontal: "right", vertical: "top" }}
                transformOrigin={{ horizontal: "left", vertical: "top" }}
                PaperProps={{
                  style: {
                    borderRadius: 8,
                    minWidth: 220,
                    boxShadow:
                      "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                    marginLeft: 8,
                  },
                }}
              >
                <MenuItem
                  onClick={() => handleUnlinkType(menuFormId, hoverType?.id)}
                >
                  Unlink
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    // handleCopyLink(menuFormId, hoverType.id);
                    closeAllMenus();
                  }}
                >
                  Copy Link
                </MenuItem>
              </Menu>
            </div>

            {/* Fields & Docs Meta */}
            <div className="intake-card-meta-row">
              <span className="intake-card-meta">
                📝 {form.fieldsCount} fields
              </span>
              <span className="intake-card-meta" style={{ marginLeft: 8 }}>
                📎 {form.documentsCount} documents
              </span>
            </div>

            {/* Description */}
            <div className="intake-card-desc">{form.description}</div>

            {/* Status */}
            <div className="intake-card-status-row">
              <span className="intake-card-status-label">Status:</span>
              <span
                className="intake-card-status"
                style={{
                  color: form.attachedTypes.length > 0 ? "#219653" : "#EB5757",
                }}
              >
                {form.attachedTypes.length > 0 ? "Linked" : "Unlinked"}
              </span>
            </div>

            {/* Attached Types Preview */}
            <div className="intake-card-attached-row">
              <span className="intake-card-attached-label">Attached to:</span>
              {form.attachedTypes.length > 0 ? (
                <ul className="intake-card-attached-list">
                  {form.attachedTypes.map((t, i) => (
                    <li key={i}>{t.name}</li>
                  ))}
                </ul>
              ) : (
                <span className="intake-card-attached-none">None</span>
              )}
            </div>

            {/* View Fields Link */}
            <div className="intake-card-footer-row">
              <button
                style={{ display: "flex", alignItems: "center" }}
                onClick={() =>
                  navigate("/attorney/intake/form-builder", {
                    state: { templateId: form.id },
                  })
                }
              >
                <img
                  src={ViewIcon}
                  alt="View Fields"
                  style={{
                    width: "24px",
                    height: "24px",
                    marginRight: "10px",
                  }}
                />
                <p className="view-fields-edit-btn">View Fields</p>
              </button>
              <IconButton
                size="small"
                onClick={(e) => handleMenuClick(e, form.id)}
              >
                <MoreVertIcon />
              </IconButton>
            </div>
          </div>
        ))}
      </div>

      <div className="intake-help-link">Need help?</div>
      <Dialog
        open={confirmDialog.open}
        onClose={() =>
          setConfirmDialog({ open: false, typeId: null, typeName: "" })
        }
      >
        <DialogTitle>Replace Existing Intake Form?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The appointment type "{confirmDialog.typeName}" already has an
            intake form attached ("{confirmDialog.existingFormName}"). Each
            appointment type can only have one active intake form at a time.
            <br />
            <br />
            Proceeding will automatically remove the existing form and attach
            this one instead.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setConfirmDialog({ open: false, typeId: null, typeName: "" })
            }
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmReplace}
            variant="contained"
            color="primary"
          >
            Replace Form
          </Button>
        </DialogActions>
      </Dialog>
      <AiFormGenerator
        open={aiPopupOpen}
        onClose={() => setAiPopupOpen(false)}
        onGenerate={async ({
          description,
          formName,
          appointmentType,
          requiredDocs,
        }) => {
          setLoading(true);
          try {
            const res = await fetch(
              "/api/attorney/webapp/intake/forms/generateIntakeForm",
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  description,
                  formName,
                  appointmentType,
                  requiredDocs,
                  lawyerId,
                }),
              }
            );
            if (!res.ok) {
              const { error } = await res.json();
              alert("AI form generation failed: " + (error || res.statusText));
            } else {
              const data = await res.json();
              setAiPopupOpen(false);
              navigate("/attorney/settings/form-preview", {
                state: {
                  schema: data.parsed_json,
                  description,
                  formName,
                  appointmentType,
                  requiredDocs,
                },
              });
            }
          } catch (err) {
            alert("Something went wrong generating the form.");
            console.error(err);
          } finally {
            setLoading(false);
          }
        }}
      />
      <UploadForm
        open={uploadPopupOpen}
        onClose={() => setUploadPopupOpen(false)}
        onNext={(parsed_json) => {
          setUploadPopupOpen(false);
          navigate("/attorney/settings/form-preview", {
            state: {
              schema: parsed_json,
              // Optionally pass other info if needed
            },
          });
        }}
      />
    </div>
  );
}
